import Vue from 'vue'
import VueI18n from 'vue-i18n'
import { Settings } from 'luxon'

/**
 * This is the master ordered list of languages for the user to select from. This is used internally
 * and in the language selection in the UI.
 *
 * The first language specified is the default and is automatically loaded as the fallback language.
 */
export const languageSelections = [
  { code: 'en', region: 'US', iso: 'en-US', file: 'en-setup.js', name: 'English' },
  { code: 'fr', region: 'FR', iso: 'fr-FR', file: 'fr-setup.js', name: 'Français' },
  { code: 'de', region: 'DE', iso: 'de-DE', file: 'de-setup.js', name: 'Deutsch' },
  { code: 'es', region: 'ES', iso: 'es-ES', file: 'es-setup.js', name: 'Español' },
  { code: 'it', region: 'IT', iso: 'it-IT', file: 'it-setup.js', name: 'Italiano' },
  { code: 'ja', region: 'JP', iso: 'ja-JP', file: 'ja-setup.js', name: '日本語 (Japanese)' },
  { code: 'pt', region: 'BR', iso: 'pt-BR', file: 'pt-BR-setup.js', name: 'Português Brasileiro' },
]

const defaultLanguageMessages = require(`./${languageSelections[0].file}`).default
const loadedLanguages = [languageSelections[0].iso]
let i18n = null

/**
 * Get the selected language from route. Falls back to en-US if not found.
 *
 * @returns The selected language record
 */
const readSelectedLanguage = () => {
  const pathname = window.location.pathname
  const segments = pathname.split('/')

  // Access the lang segment
  const currentLang = segments[1] ?? 'en'
  const selectedLanguage = languageSelections.find(lang => lang.code === currentLang)
  return selectedLanguage || languageSelections[0]
}

/**
 * Uses the cached language definition and sets it as current, or if not cached, dynamically loads the
 * specified language.
 *
 * @param {string} lang Language record from languageSelections
 */
export const setActiveLanguage = async (lang = readSelectedLanguage()) => {
  if (!loadedLanguages.includes(lang.code)) {
    const messages = await import(/* webpackChunkName: "lang-[request]" */ `./${lang.file}`)
    loadedLanguages.push(lang.code)

    i18n.setLocaleMessage(lang.iso, messages.default)
  }
  setI18nLanguage(lang.iso)
}

/**
 * Set the i18n plugin language
 * Makes the specified language work throughout the app.
 *
 * @param {string} language The language abbreviation
 */
export const setI18nLanguage = language => {
  // I18n plugin
  i18n.locale = language
  // Luxon dates
  Settings.defaultLocale = language
  Settings.resetCaches()
}

Vue.use(VueI18n)
i18n = new VueI18n({
  locale: readSelectedLanguage().code,
  fallbackLocale: languageSelections[0].code,
  messages: { [languageSelections[0].iso]: defaultLanguageMessages },
  silentTranslationWarn: true,
})

// Initialise with the default or preselected active language.
setActiveLanguage()

export default i18n
